/**
 * Mock exams (« examens blancs »): the numbered series and the urgent session exams prepared by
 * the AI for one centre (sent from the Sessions tab), their official structure, status.
 */

import {
  type ContentStatusEnum,
  type MockExam,
  useAcademyFormatRetrieve,
  useAcademyMockExamsList,
  useAcademyMockExamsPartialUpdate,
  useAcademyMockExamsRetrieve,
} from "@agencyos/api-client";
import { contentLanguage } from "@agencyos/core";
import { Badge, Button, GlassCard, GlassSheet, SelectField, Skeleton, TextField } from "@agencyos/ui-web";
import { CheckCircle2, MapPin, Save, TriangleAlert } from "lucide-react";
import { type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";
import { dateTimeText } from "../billing/shared";
import { type ExamSkill, expectedStructure, stringList, structureCheck } from "./academyData";
import { useAdminExam } from "./examContext";
import { listeningShape } from "./ieltsData";
import { useIeltsSkillName } from "./ieltsShared";
import { CONTENT_STATUSES, ErrorList, isObject, Pager, StatusBadge, useAcademyRoles, useRefreshAcademy } from "./shared";

/** The official structure, from the versioned format table (never written in the code). */
export function useExpectedStructure(): Record<ExamSkill, number> | null | undefined {
  const { params } = useAdminExam();
  const format = useAcademyFormatRetrieve(params);
  if (format.data === undefined) return format.isError ? null : undefined;
  return format.data.status === 200 ? expectedStructure(format.data.data.sections) : null;
}

function ConformityBadge({ exam, expected }: { exam: MockExam; expected: Record<ExamSkill, number> | null | undefined }) {
  const { t } = useTranslation();
  if (!expected) return <span className="muted">—</span>;
  const check = structureCheck(exam.counts, expected);
  // IELTS: a question can be worth several marks (one per gap); the server only publishes a mock
  // whose marks follow the official structure, so a published IELTS mock is compliant.
  const ok = check.ok || ((exam.exam === "ielts_general" || exam.exam === "tef_canada") && exam.status === "published");
  return ok ? (
    <Badge tone="success">
      <CheckCircle2 size={14} aria-hidden="true" /> {t("academyAdmin.mocks.compliant")}
    </Badge>
  ) : (
    <Badge tone="danger">
      <TriangleAlert size={14} aria-hidden="true" /> {t("academyAdmin.mocks.nonCompliant")}
    </Badge>
  );
}

function itemsOf(value: unknown, skill: ExamSkill): string[] {
  return isObject(value) ? stringList(value[skill]) : [];
}

/** « Examen blanc n° 3 » (numbered series) or « Session · Yaoundé » (urgent exam of a centre). */
function KindCell({ exam }: { exam: MockExam }) {
  const { t } = useTranslation();
  if (exam.kind === "session") {
    return (
      <Badge tone="offer">
        <MapPin size={12} aria-hidden="true" /> {t("academyAdmin.mocks.sessionOf", { center: exam.center || t("academyAdmin.sessions.allCenters") })}
      </Badge>
    );
  }
  return <Badge>{t("academyAdmin.mocks.numbered")}</Badge>;
}

function MockExamSheet({ id, expected, onClose }: { id: string; expected: Record<ExamSkill, number> | null | undefined; onClose: () => void }) {
  const { t } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const detail = useAcademyMockExamsRetrieve(id);
  const exam = detail.data?.status === 200 ? detail.data.data : undefined;
  return (
    <GlassSheet
      open
      title={exam ? (exam.kind === "session" ? exam.title : t("academyAdmin.mocks.sheetTitle", { n: exam.number ?? "" })) : t("academyAdmin.mocks.title")}
      onClose={onClose}
    >
      {exam ? <MockExamForm key={exam.id} exam={exam} expected={expected} canEdit={canEdit} onSaved={refresh} /> : <Skeleton style={{ height: 240 }} />}
    </GlassSheet>
  );
}

function MockExamForm({
  exam,
  expected,
  canEdit,
  onSaved,
}: {
  exam: MockExam;
  expected: Record<ExamSkill, number> | null | undefined;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { ielts } = useAdminExam();
  const skillName = useIeltsSkillName();
  const update = useAcademyMockExamsPartialUpdate({ mutation: { onSuccess: onSaved } });
  const session = exam.kind === "session";
  const [title, setTitle] = useState(exam.title);
  const [theme, setTheme] = useState(exam.session_theme ?? "");
  const [status, setStatus] = useState<ContentStatusEnum>(exam.status ?? "draft");
  const check = expected ? structureCheck(exam.counts, expected) : null;
  // The IELTS structure counts marks, checked by the server when publishing.
  const blocked = !ielts && status === "published" && check !== null && !check.ok;
  const errors = fieldErrors(update.error);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (blocked) return;
    update.mutate({ id: exam.id, data: { title: title.trim(), session_theme: theme.trim(), status } });
  };

  return (
    <form className="stack" onSubmit={onSubmit} noValidate>
      <div className="row">
        <KindCell exam={exam} />
        <StatusBadge status={exam.status ?? "draft"} />
        <ConformityBadge exam={exam} expected={expected} />
      </div>
      {session ? (
        <p role="note" className="notice-warning installment-row">
          {exam.sent_at
            ? t("academyAdmin.mocks.sessionSent", { date: dateTimeText(exam.sent_at, i18n.language), count: exam.notified })
            : t("academyAdmin.mocks.sessionDraft")}
        </p>
      ) : null}
      <section className="stack" aria-labelledby={`mock-structure-${exam.id}`}>
        <h3 id={`mock-structure-${exam.id}`}>{t("academyAdmin.mocks.structure")}</h3>
        <p className="muted">{ielts ? t("academyAdmin.ielts.mocks.structureHint") : t("academyAdmin.mocks.structureHint")}</p>
        {check ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.skillLabel")}</th>
                  <th>{t("academyAdmin.mocks.found")}</th>
                  <th>{t("academyAdmin.mocks.expected")}</th>
                  <th>{t("academyAdmin.mocks.check")}</th>
                </tr>
              </thead>
              <tbody>
                {check.rows.map((row) => (
                  <tr key={row.skill}>
                    <td>{ielts ? skillName(row.skill) : t(`academyAdmin.skill.${row.skill}`)}</td>
                    <td>{row.count}</td>
                    <td>{row.expected}</td>
                    <td>{row.ok ? <span className="ok">{t("academyAdmin.mocks.rowOk")}</span> : <span className="ko">{t("academyAdmin.mocks.rowKo")}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">{t("academyAdmin.mocks.noFormat")}</p>
        )}
        <div className="grid-2">
          {(["co", "ce"] as const).map((skill) => (
            <p key={skill} className="installment-row">
              <strong>{ielts ? skillName(skill) : skill.toUpperCase()}</strong> · {t("academyAdmin.mocks.questions", { count: itemsOf(exam.questions, skill).length })}
            </p>
          ))}
          {(["ee", "eo"] as const).map((skill) => (
            <p key={skill} className="installment-row">
              <strong>{ielts ? skillName(skill) : skill.toUpperCase()}</strong> · {t("academyAdmin.mocks.tasks", { count: itemsOf(exam.tasks, skill).length })}
            </p>
          ))}
        </div>
      </section>
      <fieldset className="plain-fieldset stack" disabled={!canEdit || session}>
        <div className="form-grid">
          <TextField label={t("academyAdmin.mocks.examTitle")} value={title} maxLength={120} error={errors.title} onChange={(e) => setTitle(e.target.value)} />
          <TextField
            label={t("academyAdmin.mocks.sessionTheme")}
            hint={t("academyAdmin.mocks.sessionThemeHint")}
            value={theme}
            maxLength={200}
            error={errors.session_theme}
            onChange={(e) => setTheme(e.target.value)}
          />
          <SelectField
            label={t("academyAdmin.statusLabel")}
            value={status}
            error={errors.status}
            options={CONTENT_STATUSES.map((s) => ({ value: s, label: t(`academyAdmin.status.${s}`) }))}
            onChange={(e) => setStatus(e.target.value as ContentStatusEnum)}
          />
        </div>
        {blocked ? (
          <p role="alert" className="notice-warning installment-row">
            {t("academyAdmin.mocks.publishBlocked")}
          </p>
        ) : null}
        {canEdit && !session ? (
          <div className="row">
            <Button type="submit" icon={<Save size={16} aria-hidden="true" />} disabled={update.isPending || blocked}>
              {t("academyAdmin.mocks.save")}
            </Button>
          </div>
        ) : null}
      </fieldset>
      <ErrorList error={update.error} />
      {update.isSuccess ? (
        <span role="status" className="ok">
          {t("academyAdmin.mocks.saved")}
        </span>
      ) : null}
    </form>
  );
}

export function MockExamsTab() {
  const { t } = useTranslation();
  const { exam: examCode, ielts, params, contentParams } = useAdminExam();
  const skillName = useIeltsSkillName();
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const close = useCallback(() => setOpen(null), []);
  const list = useAcademyMockExamsList({ page, ...contentParams });
  const expected = useExpectedStructure();
  const format = useAcademyFormatRetrieve(params);
  const shape = ielts && format.data?.status === 200 ? listeningShape(format.data.data.sections) : null;
  const data = list.data?.status === 200 ? list.data.data : undefined;
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("academyAdmin.mocks.title")}</h2>
          <p className="muted">{ielts ? t("academyAdmin.ielts.mocks.intro") : examCode === "tcf_canada" ? t("academyAdmin.mocks.intro") : t("academyAdmin.mocks.introExam", { exam: t(`ielts.exams.${examCode}.name`) })}</p>
        </div>
      </div>
      {expected && ielts ? (
        <p className="muted">
          {t("academyAdmin.ielts.mocks.officialStructure", { parts: shape?.parts ?? "—", perPart: shape?.perPart ?? "—", ce: expected.ce, ee: expected.ee, eo: expected.eo })}
        </p>
      ) : expected ? (
        <p className="muted">
          {t("academyAdmin.mocks.officialStructure", { co: expected.co, ce: expected.ce, ee: expected.ee, eo: expected.eo })}
        </p>
      ) : null}
      {data === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : data.results.length ? (
        <>
          <p className="muted">{t("academyAdmin.count", { count: data.count })}</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.mocks.kind")}</th>
                  <th>{t("academyAdmin.mocks.examTitle")}</th>
                  <th>{t("academyAdmin.mocks.sessionTheme")}</th>
                  <th>{t("academyAdmin.mocks.content")}</th>
                  <th>{t("academyAdmin.mocks.check")}</th>
                  <th>{t("academyAdmin.statusLabel")}</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((exam) => (
                  <tr key={exam.id}>
                    <td>
                      <div className="stack academy-kind">
                        <KindCell exam={exam} />
                        {exam.kind !== "session" && exam.number !== null && exam.number !== undefined ? (
                          <span className="nowrap">{t("academyAdmin.mocks.numberValue", { n: exam.number })}</span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <button type="button" className="link" onClick={() => setOpen(exam.id)}>
                        {exam.title}
                      </button>
                    </td>
                    <td lang={contentLanguage(exam.exam)}>{exam.session_theme || "—"}</td>
                    <td className="nowrap">
                      {(["co", "ce", "ee", "eo"] as const).map((s) => (
                        <span key={s} className="code-chip" title={ielts ? skillName(s) : undefined}>
                          {ielts ? t(`academyAdmin.ielts.skillShort.${s}`) : s.toUpperCase()} {exam.counts?.[s] ?? 0}
                        </span>
                      ))}
                    </td>
                    <td>
                      <ConformityBadge exam={exam} expected={expected} />
                    </td>
                    <td>
                      <div className="stack academy-kind">
                        <StatusBadge status={exam.status ?? "draft"} />
                        {exam.sent_at ? <span className="muted nowrap">{t("academyAdmin.mocks.sentCount", { count: exam.notified })}</span> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </>
      ) : (
        <p className="muted">{t("academyAdmin.mocks.empty")}</p>
      )}
      {open ? <MockExamSheet key={open} id={open} expected={expected} onClose={close} /> : null}
    </GlassCard>
  );
}
